import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTerminalAI, DEFAULT_CONTEXT_LINES, DEFAULT_CONTEXT_LINES_PRO } from '../../hooks/useTerminalAI'
import TerminalAIButton from '../../components/TerminalAI/TerminalAIButton'
import TerminalAIPanel from '../../components/TerminalAI/TerminalAIPanel'
import { 
  ArrowLeft, 
  Shield, 
  Network, 
  Route as RouteIcon, 
  Wifi, 
  Terminal,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Edit,
  X,
  Trash2,
  Power,
  PowerOff,
  Play,
  Square
} from 'lucide-react'
import api from '../../services/api'
import { routersApi } from '../../services/managedDevicesApi'
import { routerOsWebSocketService } from '../../services/routerosWebSocketService'
import clsx from 'clsx'
import Modal from '../../components/Modal/Modal'
import { usePopupWindowDocumentChrome, ROUTER_MANAGEMENT_FAVICON_HREF } from '../../utils/popupWindowDocumentChrome'
import { useRole } from '../../hooks/useRole'

const TABS = {
  FIREWALL: 'firewall',
  NAT: 'nat',
  ROUTES: 'routes',
  INTERFACES: 'interfaces',
  TERMINAL: 'terminal'
}

/** apiUsername / apiPasswordTemporaria + apiPassword (compat nomes antigos) */
function routerApiUsername(r) {
  if (!r) return 'admin'
  return r.apiUsername || r.routerOsApiUsername || 'admin'
}
function routerApiPasswordForConnection(r) {
  if (!r) return ''
  const temp = (r.apiPasswordTemporaria || r.routerOsApiPassword || '').trim()
  const perm = (r.apiPassword || r.automaisApiPassword || '').trim()
  return temp || perm
}

export default function RouterManagement() {
  const { t } = useTranslation()
  const { canDelete } = useRole()
  const { routerId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isPopupManagementRoute = location.pathname.startsWith('/router-management/')
  const [activeTab, setActiveTab] = useState(TABS.FIREWALL)
  const [connectionStatus, setConnectionStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState({})
  const [terminalCommand, setTerminalCommand] = useState('')
  const [terminalHistory, setTerminalHistory] = useState([])
  const [executingCommand, setExecutingCommand] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null) // Item selecionado (clique simples)
  const [selectedItemForDetails, setSelectedItemForDetails] = useState(null) // Item para detalhes (duplo clique)
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [systemInfo, setSystemInfo] = useState(null)
  const [updatingSystemInfo, setUpdatingSystemInfo] = useState(false)
  const [router, setRouter] = useState(null)
  const wsConnectedRef = useRef(false)
  const terminalHistoryRef = useRef([])
  terminalHistoryRef.current = terminalHistory
  const contextEntriesRef = useRef(DEFAULT_CONTEXT_LINES)

  const getTerminalBufferMgmt = useCallback(() => {
    return terminalHistoryRef.current
      .slice(-contextEntriesRef.current)
      .map((e) => (e.type === 'command' ? `> ${e.content}` : e.content))
      .join('\n')
  }, [])

  const executeRouterCommandMgmt = useCallback(
    async (command) => {
      if (!router) return t('routers.management.routerNotLoaded')
      const routerIp = connectionStatus?.routerIp || getRouterIp(router)
      if (!routerIp) return t('routers.management.ipNotFound')

      const username = routerApiUsername(router)
      const password = routerApiPasswordForConnection(router)

      setTerminalHistory((prev) => [
        ...prev,
        { type: 'command', content: command, source: 'ai' },
      ])

      try {
        const result = await routerOsWebSocketService.executeCommand(
          routerId, routerIp, username, password, command
        )
        const formatted = formatTerminalResult(result.data || result)
        setTerminalHistory((prev) => [...prev, { type: 'result', content: formatted }])
        return formatted
      } catch (e) {
        const errMsg = e.message || t('routers.management.executeError')
        setTerminalHistory((prev) => [...prev, { type: 'error', content: errMsg }])
        return t('routers.management.errorPrefix', { msg: errMsg })
      }
    },
    [router, routerId, connectionStatus, t]
  )

  const ai = useTerminalAI({
    systemProfileSlug: 'MikrotikRouterOS',
    deviceId: routerId,
    deviceName: router?.name,
    getTerminalBuffer: getTerminalBufferMgmt,
    executeCommand: executeRouterCommandMgmt,
  })
  contextEntriesRef.current = ai.settings.proMode ? DEFAULT_CONTEXT_LINES_PRO : DEFAULT_CONTEXT_LINES

  usePopupWindowDocumentChrome({
    title: router?.name ? `${router.name} · MikroTik · Automais` : t('routers.management.popupTitle'),
    faviconHref: ROUTER_MANAGEMENT_FAVICON_HREF,
    enabled: isPopupManagementRoute,
  })

  // Colunas do Winbox para cada tipo
  const WINBOX_COLUMNS = {
    firewall: ['chain', 'action', 'src-address', 'dst-address', 'protocol', 'dst-port', 'in-interface', 'out-interface', 'comment'],
    nat: ['chain', 'action', 'src-address', 'dst-address', 'protocol', 'dst-port', 'to-addresses', 'to-ports', 'comment'],
    routes: ['dst-address', 'gateway', 'distance', 'scope', 'target-scope', 'routing-mark', 'interface', 'comment'],
    interfaces: ['name', 'type', 'mtu', 'mac-address', 'rx-byte', 'tx-byte', 'rx-packet', 'tx-packet', 'running', 'disabled', 'comment']
  }

  useEffect(() => {
    loadRouterData()
    return () => {
      // Desconectar WebSocket ao desmontar componente
      if (wsConnectedRef.current) {
        routerOsWebSocketService.disconnect()
        wsConnectedRef.current = false
      }
    }
  }, [routerId])

  const loadRouterData = async () => {
    try {
      const routerData = await routersApi.getById(routerId)
      setRouter(routerData)
      await checkConnection(routerData)
    } catch (err) {
      setError(t('routers.management.loadRouterError'))
      setConnectionStatus({ connected: false })
    }
  }

  const getRouterIp = (routerData) => {
    // Tentar obter do RouterOsApiUrl
    if (routerData?.routerOsApiUrl) {
      // Remover protocolo se presente (http:// ou https://)
      let url = routerData.routerOsApiUrl.replace(/^https?:\/\//, '')
      // Pegar apenas o hostname/IP (antes de : ou /)
      const parts = url.split(':')
      if (parts.length > 0 && parts[0]) {
        const ip = parts[0].split('/')[0].trim()
        if (ip) {
          return ip
        }
      }
    }
    // Se não tiver, o backend pode obter o IP via peer VPN na API
    return null
  }

  const checkConnection = async (routerData = router) => {
    if (!routerData) {
      setError(t('routers.management.routerDataUnavailable'))
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Conectar ao WebSocket via API C# usando routerId
      // A API C# fará o proxy para o routeros.io Python baseado no ServerEndpoint da VpnNetwork
      if (!routerOsWebSocketService.isConnected() || routerOsWebSocketService.getCurrentRouterId() !== routerId) {
        // Desconectar se já estava conectado a um router diferente
        if (routerOsWebSocketService.isConnected()) {
          await routerOsWebSocketService.disconnect()
        }
        await routerOsWebSocketService.connect(routerId)
        wsConnectedRef.current = true
        // Aguardar o proxy C#→Python estabilizar antes de enviar get_status (evita mensagem perdida)
        await new Promise(r => setTimeout(r, 500))
      }

      // Obter IP do router (pode ser null - o backend buscará do peer VPN se necessário)
      const routerIp = getRouterIp(routerData)

      // Obter status via WebSocket (routerIp pode ser null - backend buscará se necessário)
      const status = await routerOsWebSocketService.getStatus(routerId, routerIp)
      
      // O backend retorna router_ip na resposta quando obtém do peer VPN
      const finalRouterIp = status.router_ip || routerIp
      
      setConnectionStatus({
        connected: status.connected || false,
        success: status.success || false,
        routerIp: finalRouterIp,
        identity: status.identity,
        resource: status.resource,
        error: status.error
      })
      if (!status.connected && status.error) {
        setError(status.error)
      }

      // Se conectou, atualizar informações do sistema
      if (status.connected && status.resource) {
        const resource = status.resource
        setSystemInfo({
          model: resource['board-name'] || resource['board-name'] || '-',
          serialNumber: routerData.serialNumber || '-',
          firmwareVersion: resource.version || '-',
          hardwareInfo: {
            cpuLoad: resource['cpu-load'] || resource['cpu-load'] || '-',
            memoryUsage: resource['free-memory'] && resource['total-memory'] 
              ? `${Math.round((1 - resource['free-memory'] / resource['total-memory']) * 100)}%`
              : '-',
            uptime: resource.uptime || '-'
          }
        })
      }
    } catch (err) {
      setError(err.message || t('routers.management.connectError'))
      setConnectionStatus({ connected: false, success: false, error: err.message })
    } finally {
      setLoading(false)
    }
  }

  // Função para atualizar status do sistema (usada tanto no useEffect quanto no botão)
  const refreshSystemInfo = useCallback(async () => {
    // Recarregar status (que já traz informações do sistema)
    // Não mostrar loading durante atualizações automáticas
    if (!connectionStatus?.connected || !router) return
    
    // Verificar se WebSocket está conectado antes de tentar
    if (!routerOsWebSocketService.isConnected()) {
      // Tentar reconectar se tiver routerId
      if (routerId && !routerOsWebSocketService.getState().includes('CONNECTING')) {
        try {
          await routerOsWebSocketService.connect(routerId)
        } catch (err) {
          // Ignorar erro silenciosamente
        }
      }
      return
    }
    
    try {
      const routerIp = connectionStatus.routerIp || getRouterIp(router)
      if (!routerIp) return

      // Obter status via WebSocket (com timeout curto para não bloquear)
      const status = await routerOsWebSocketService.getStatus(routerId, routerIp)
      
      // Atualizar connectionStatus com novos dados
      setConnectionStatus(prev => ({
        ...prev,
        routerIp: status.router_ip || prev.routerIp,
        identity: status.identity || prev.identity,
        resource: status.resource || prev.resource,
        error: status.error
      }))
      
      // Atualizar informações do sistema se conectado
      if (status.connected && status.resource) {
        const resource = status.resource
        setSystemInfo({
          model: resource['board-name'] || resource['board-name'] || '-',
          serialNumber: router?.serialNumber || '-',
          firmwareVersion: resource.version || '-',
          hardwareInfo: {
            cpuLoad: resource['cpu-load'] || resource['cpu-load'] || '-',
            memoryUsage: resource['free-memory'] && resource['total-memory'] 
              ? `${Math.round((1 - resource['free-memory'] / resource['total-memory']) * 100)}%`
              : '-',
            uptime: resource.uptime || '-'
          }
        })
      }
    } catch (err) {
      // Silenciar erros durante atualizações automáticas para não poluir a UI
      if (err.message?.includes('WebSocket não está conectado') || err.message?.includes('Timeout')) {
        // Atualizar status de conexão
        setConnectionStatus(prev => ({
          ...prev,
          connected: false,
          error: err.message || t('routers.management.websocketDisconnected')
        }))
        
        // Tentar reconectar se foi timeout
        if (err.message?.includes('Timeout') && routerId) {
          try {
            routerOsWebSocketService.forceDisconnect()
            await routerOsWebSocketService.connect(routerId, true)
          } catch (reconnectErr) {
            // Ignorar erro silenciosamente
          }
        }
      }
    }
  }, [connectionStatus?.connected, connectionStatus?.routerIp, router, routerId, t])

  // Atualizar status do sistema a cada 5 segundos (reduzido de 1s para evitar sobrecarga)
  useEffect(() => {
    if (!connectionStatus?.connected || !router) return

    const intervalId = setInterval(() => {
      refreshSystemInfo()
    }, 5000) // Atualizar a cada 5 segundos (reduzido para evitar sobrecarga)

    return () => {
      clearInterval(intervalId)
    }
  }, [connectionStatus?.connected, router, refreshSystemInfo])

  // Função para carregar dados da aba (usada tanto no useEffect quanto em outras funções)
  const loadTabData = useCallback(async (showLoading = true) => {
    if (!connectionStatus?.connected || !router) return

    try {
      if (showLoading) {
        setLoading(true)
      }
      setError(null)

      // Usar routerIp do connectionStatus (já foi buscado pelo backend se necessário)
      const routerIp = connectionStatus.routerIp || getRouterIp(router)
      if (!routerIp) {
        // Se não tem routerIp, tentar buscar novamente do status
        console.warn('routerIp não encontrado, tentando buscar do status novamente...')
        if (showLoading) {
          setError(t('routers.management.waitingRouterIp'))
        }
        return
      }

      const username = routerApiUsername(router)
      const password = routerApiPasswordForConnection(router)

      let command = ''
      switch (activeTab) {
        case TABS.FIREWALL:
          command = '/ip/firewall/filter/print'
          break
        case TABS.NAT:
          command = '/ip/firewall/nat/print'
          break
        case TABS.ROUTES:
          command = '/ip/route/print'
          break
        case TABS.INTERFACES:
          command = '/interface/print'
          break
        default:
          return
      }

      // Executar comando via WebSocket
      const result = await routerOsWebSocketService.executeCommand(
        routerId,
        routerIp,
        username,
        password,
        command
      )

      // Processar resultado
      if (result.success !== false && result.data) {
        const items = Array.isArray(result.data) ? result.data : [result.data]
        
        switch (activeTab) {
          case TABS.FIREWALL:
            setData({ rules: items })
            break
          case TABS.NAT:
            setData({ rules: items })
            break
          case TABS.ROUTES:
            setData({ routes: items })
            break
          case TABS.INTERFACES:
            setData({ interfaces: items })
            break
        }
      } else {
        throw new Error(result.error || t('routers.management.executeCommandError'))
      }
    } catch (err) {
      // Só mostrar erro se não for atualização automática silenciosa
      if (showLoading) {
        setError(err.message || t('routers.management.loadDataError'))
      } else {
        // Erro ao atualizar dados da aba (silencioso)
      }
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }, [activeTab, connectionStatus?.connected, connectionStatus?.routerIp, router, routerId, t])

  // Atualizar dados da aba ativa a cada 1 segundo
  useEffect(() => {
    if (!connectionStatus?.connected || !router) return

    // Carregar dados imediatamente (com loading)
    loadTabData(true)

    const intervalId = setInterval(() => {
      // Atualizar sem mostrar loading (atualização silenciosa)
      loadTabData(false)
    }, 1000) // Atualizar a cada 1 segundo

    return () => {
      clearInterval(intervalId)
    }
  }, [activeTab, connectionStatus?.connected, router, loadTabData])

  const formatAsTable = (items) => {
    const keys = new Set()
    items.forEach((item) =>
      Object.keys(item).forEach((k) => {
        if (k !== '.id' && !k.startsWith('=')) keys.add(k)
      })
    )
    const cols = Array.from(keys)
    if (cols.length === 0) return t('routers.management.noData')

    const widths = {}
    cols.forEach((c) => { widths[c] = c.length })
    items.forEach((item) => {
      cols.forEach((c) => {
        const len = String(item[c] ?? '').length
        if (len > widths[c]) widths[c] = Math.min(len, 40)
      })
    })

    const header = cols.map((c) => c.padEnd(widths[c])).join('  ')
    const sep = cols.map((c) => '-'.repeat(widths[c])).join('  ')
    const rows = items.map((item) =>
      cols.map((c) => {
        const v = String(item[c] ?? '')
        return v.length > widths[c]
          ? v.substring(0, widths[c] - 1) + '…'
          : v.padEnd(widths[c])
      }).join('  ')
    )
    return [header, sep, ...rows].join('\n')
  }

  const formatAsKeyValue = (obj) => {
    const entries = Object.entries(obj).filter(
      ([k]) => !k.startsWith('.') && !k.startsWith('=') && k !== 'id' && k !== 'success' && k !== 'action'
    )
    if (entries.length === 0) return ''
    const maxKey = Math.max(...entries.map(([k]) => k.length))
    return entries
      .map(([k, v]) => {
        const val = v != null && typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')
        return `  ${k.padEnd(maxKey)} : ${val}`
      })
      .join('\n')
  }

  const formatTerminalResult = (data) => {
    if (data == null) return ''
    if (typeof data === 'string') return data
    if (typeof data === 'number' || typeof data === 'boolean') return String(data)

    if (Array.isArray(data)) {
      if (data.length === 0) return t('routers.management.noResults')
      if (typeof data[0] === 'object' && data[0] !== null) return formatAsTable(data)
      return data.map(String).join('\n')
    }

    if (typeof data === 'object') {
      const items = data.data ?? data.result
      if (Array.isArray(items)) return formatTerminalResult(items)
      if (items != null && typeof items === 'object') return formatAsKeyValue(items)
      if (typeof items === 'string') return items
      if (data.message) return data.message
      if (data.success !== undefined && items == null) {
        return data.success ? t('routers.management.commandOk') : (data.error || t('common.status.error'))
      }
      return formatAsKeyValue(data)
    }

    return String(data)
  }

  const executeTerminalCommand = async () => {
    if (!terminalCommand.trim() || executingCommand || !router) return

    try {
      setExecutingCommand(true)
      setError(null)

      // Usar routerIp do connectionStatus (já foi buscado pelo backend se necessário)
      const routerIp = connectionStatus?.routerIp || getRouterIp(router)
      if (!routerIp) {
        throw new Error(t('routers.management.ipNotFoundError'))
      }

      const username = routerApiUsername(router)
      const password = routerApiPasswordForConnection(router)

      // Executar comando via WebSocket
      const result = await routerOsWebSocketService.executeCommand(
        routerId,
        routerIp,
        username,
        password,
        terminalCommand
      )

      const formattedResult = formatTerminalResult(result.data || result)

      setTerminalHistory(prev => [
        ...prev,
        { type: 'command', content: terminalCommand },
        { type: 'result', content: formattedResult }
      ])

      setTerminalCommand('')
    } catch (err) {
      setError(err.message || t('routers.management.executeCommandError'))
      setTerminalHistory(prev => [
        ...prev,
        { type: 'command', content: terminalCommand },
        { type: 'error', content: err.message }
      ])
    } finally {
      setExecutingCommand(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      executeTerminalCommand()
    }
  }

  const handleRowClick = (item) => {
    // Clique simples: seleciona item para mostrar botões de ação
    setSelectedItem(item)
  }

  const handleRowDoubleClick = (item) => {
    // Duplo clique: abre modal de detalhes
    setSelectedItemForDetails(item)
    setIsConfigModalOpen(true)
  }

  const canItemBeDisabled = (item) => {
    // Verificar se o item pode ser desativado (tem campo 'disabled' ou é dinâmico)
    return item && ('disabled' in item || 'dynamic' in item)
  }

  const isItemDisabled = (item) => {
    return item?.disabled === 'true' || item?.disabled === true
  }

  const getEnableCommand = (tab, itemId) => {
    switch (tab) {
      case TABS.FIREWALL:
        return `/ip/firewall/filter/enable .id=${itemId}`
      case TABS.NAT:
        return `/ip/firewall/nat/enable .id=${itemId}`
      case TABS.ROUTES:
        return `/ip/route/enable .id=${itemId}`
      case TABS.INTERFACES:
        return `/interface/enable .id=${itemId}`
      default:
        return ''
    }
  }

  const getDisableCommand = (tab, itemId) => {
    switch (tab) {
      case TABS.FIREWALL:
        return `/ip/firewall/filter/disable .id=${itemId}`
      case TABS.NAT:
        return `/ip/firewall/nat/disable .id=${itemId}`
      case TABS.ROUTES:
        return `/ip/route/disable .id=${itemId}`
      case TABS.INTERFACES:
        return `/interface/disable .id=${itemId}`
      default:
        return ''
    }
  }

  const getDeleteCommand = (tab, itemId) => {
    switch (tab) {
      case TABS.FIREWALL:
        return `/ip/firewall/filter/remove .id=${itemId}`
      case TABS.NAT:
        return `/ip/firewall/nat/remove .id=${itemId}`
      case TABS.ROUTES:
        return `/ip/route/remove .id=${itemId}`
      case TABS.INTERFACES:
        return `/interface/remove .id=${itemId}`
      default:
        return ''
    }
  }

  const executeRouterCommand = async (command) => {
    if (!router) throw new Error(t('routers.management.routerDataUnavailable'))

    // Usar routerIp do connectionStatus (já foi buscado pelo backend se necessário)
    const routerIp = connectionStatus?.routerIp || getRouterIp(router)
    if (!routerIp) {
      throw new Error(t('routers.management.ipNotFoundError'))
    }

    const username = routerApiUsername(router)
    const password = routerApiPasswordForConnection(router)

    return await routerOsWebSocketService.executeCommand(
      routerId,
      routerIp,
      username,
      password,
      command
    )
  }

  const handleEnableItem = async () => {
    if (!selectedItem || !selectedItem['.id'] || !router) return
    
    try {
      setActionLoading(true)
      const command = getEnableCommand(activeTab, selectedItem['.id'])
      await executeRouterCommand(command)
      setSelectedItem(null)
      await loadTabData() // Recarregar dados
    } catch (err) {
      setError(err.message || t('routers.management.enableError'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleDisableItem = async () => {
    if (!selectedItem || !selectedItem['.id'] || !router) return

    try {
      setActionLoading(true)
      const command = getDisableCommand(activeTab, selectedItem['.id'])
      await executeRouterCommand(command)
      setSelectedItem(null)
      await loadTabData() // Recarregar dados
    } catch (err) {
      setError(err.message || t('routers.management.disableError'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteItem = async () => {
    if (!selectedItem || !selectedItem['.id'] || !router) return

    if (!window.confirm(t('routers.management.deleteConfirm'))) {
      return
    }

    try {
      setActionLoading(true)
      const command = getDeleteCommand(activeTab, selectedItem['.id'])
      await executeRouterCommand(command)
      setSelectedItem(null)
      await loadTabData() // Recarregar dados
    } catch (err) {
      setError(err.message || t('routers.management.deleteError'))
    } finally {
      setActionLoading(false)
    }
  }

  const renderTable = (items, type) => {
    if (!items || items.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          {t('routers.management.noItems')}
        </div>
      )
    }

    // Usar colunas do Winbox se disponíveis, senão usar todas as colunas disponíveis
    const winboxColumns = WINBOX_COLUMNS[type] || []
    const allKeys = new Set()
    items.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== '.id' && !key.startsWith('=')) {
          allKeys.add(key)
        }
      })
    })

    // Se temos colunas do Winbox definidas, usar apenas elas (na ordem correta)
    // Senão, usar todas as colunas disponíveis
    const columns = winboxColumns.length > 0
      ? winboxColumns.filter(col => allKeys.has(col))
      : Array.from(allKeys)

    // Formatar valores para exibição
    const formatValue = (value, column) => {
      if (value === null || value === undefined || value === '') return '-'
      
      // Formatar bytes para interfaces
      if ((column === 'rx-byte' || column === 'tx-byte') && !isNaN(value)) {
        const bytes = parseInt(value)
        if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`
        if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`
        if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`
        return `${bytes} B`
      }

      return String(value)
    }

    // Obter classe CSS para destacar valores importantes
    const getValueClassName = (value, column) => {
      // Formatar booleanos (similar ao Winbox)
      if (value === 'true' || value === true) return 'text-green-600 font-bold'
      if (value === 'false' || value === false) return 'text-gray-400'
      
      // Destacar ações importantes
      if (column === 'action') {
        const actionColors = {
          'accept': 'text-green-600 font-semibold',
          'drop': 'text-red-600 font-semibold',
          'reject': 'text-orange-600 font-semibold',
          'masquerade': 'text-blue-600 font-semibold',
          'src-nat': 'text-blue-600 font-semibold',
          'dst-nat': 'text-blue-600 font-semibold'
        }
        return actionColors[String(value)?.toLowerCase()] || 'text-gray-700'
      }

      return 'text-gray-800'
    }

    return (
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-300 bg-gray-100">
              {columns.map(col => (
                <th key={col} className="text-left p-2.5 font-semibold text-gray-800 text-xs border-r border-gray-200 last:border-r-0">
                  {col.replace(/-/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr 
                key={item['.id'] || idx} 
                className={clsx(
                  "border-b border-gray-200 cursor-pointer transition-colors",
                  idx % 2 === 0 ? "bg-white" : "bg-gray-50",
                  "hover:bg-blue-100",
                  selectedItem && selectedItem['.id'] === item['.id'] && "bg-blue-200"
                )}
                onClick={() => handleRowClick(item)}
                onDoubleClick={() => handleRowDoubleClick(item)}
              >
                {columns.map(col => (
                  <td key={col} className={clsx("p-2.5 text-xs border-r border-gray-100 last:border-r-0 font-mono", getValueClassName(item[col], col))}>
                    {formatValue(item[col], col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderConfigModal = () => {
    if (!selectedItemForDetails) return null

    const formatFieldName = (key) => {
      return key
        .replace(/-/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    }

    const formatFieldValue = (value) => {
      if (value === null || value === undefined || value === '') return t('routers.management.empty')
      if (value === 'true' || value === true) return t('common.generic.yes')
      if (value === 'false' || value === false) return t('common.generic.no')
      return String(value)
    }

    return (
      <Modal
        isOpen={isConfigModalOpen}
        onClose={() => {
          setIsConfigModalOpen(false)
          setSelectedItemForDetails(null)
        }}
        title={t('routers.management.configModalTitle', { tab: activeTab.charAt(0).toUpperCase() + activeTab.slice(1) })}
      >
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">ID: {selectedItemForDetails['.id'] || 'N/A'}</p>
            <p className="text-xs text-gray-500">
              {t('routers.management.configEditHint')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(selectedItemForDetails)
              .filter(([key]) => key !== '.id' && !key.startsWith('='))
              .map(([key, value]) => (
                <div key={key} className="border-b border-gray-100 pb-2">
                  <label className="text-xs font-semibold text-gray-600 uppercase">
                    {formatFieldName(key)}
                  </label>
                  <div className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                    {formatFieldValue(value)}
                  </div>
                </div>
              ))}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              onClick={() => {
                setIsConfigModalOpen(false)
                setSelectedItemForDetails(null)
              }}
              className="btn btn-secondary btn-sm"
            >
              {t('common.buttons.close')}
            </button>
            <button
              onClick={() => {
                // TODO: Implementar edição
                alert(t('routers.management.editInDevelopment'))
              }}
              className="btn btn-primary btn-sm"
            >
              <Edit className="w-4 h-4 mr-1" />
              {t('common.buttons.edit')}
            </button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
                window.close()
                return
              }
              navigate('/routers')
            }}
            className="btn btn-secondary btn-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.buttons.back')}
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('routers.management.pageTitle')}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {router?.name || t('common.generic.loading')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Status da conexão */}
          <div className="flex items-center gap-2">
            {connectionStatus?.connected ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-600">{t('common.status.connected')}</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-sm text-red-600">{t('common.status.disconnected')}</span>
              </>
            )}
          </div>
          <button
            onClick={checkConnection}
            className="btn btn-secondary btn-sm"
            disabled={loading}
          >
            <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
            {t('routers.management.reconnect')}
          </button>
        </div>
      </div>

      {/* Informações do Sistema */}
      {connectionStatus?.connected && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('routers.management.systemInfoTitle')}</h2>
            <button
              onClick={refreshSystemInfo}
              className="btn btn-secondary btn-sm"
              disabled={updatingSystemInfo}
            >
              <RefreshCw className={clsx('w-4 h-4 mr-1', updatingSystemInfo && 'animate-spin')} />
              {t('common.generic.refresh')}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">{t('routers.management.model')}</label>
              <p className="mt-1 text-sm text-gray-900 font-mono">
                {systemInfo?.model || connectionStatus?.model || '-'}
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">{t('routers.management.serialNumber')}</label>
              <p className="mt-1 text-sm text-gray-900 font-mono">
                {systemInfo?.serialNumber || connectionStatus?.serialNumber || '-'}
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">{t('routers.management.firmware')}</label>
              <p className="mt-1 text-sm text-gray-900 font-mono">
                {systemInfo?.firmwareVersion || connectionStatus?.firmwareVersion || '-'}
              </p>
            </div>
            {(() => {
              const hardwareInfo = systemInfo?.hardwareInfo 
                ? (typeof systemInfo.hardwareInfo === 'string' 
                    ? JSON.parse(systemInfo.hardwareInfo) 
                    : systemInfo.hardwareInfo)
                : null
              
              if (!hardwareInfo) return null
              
              return (
                <>
                  {hardwareInfo.cpuLoad && (
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase">CPU Load</label>
                      <p className="mt-1 text-sm text-gray-900 font-mono">
                        {hardwareInfo.cpuLoad}%
                      </p>
                    </div>
                  )}
                  {hardwareInfo.memoryUsage && (
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase">{t('hosts.management.memory')}</label>
                      <p className="mt-1 text-sm text-gray-900 font-mono">
                        {hardwareInfo.memoryUsage}
                      </p>
                    </div>
                  )}
                  {hardwareInfo.temperature && (
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase">{t('routers.management.temperature')}</label>
                      <p className="mt-1 text-sm text-gray-900 font-mono">
                        {hardwareInfo.temperature}°C
                      </p>
                    </div>
                  )}
                  {hardwareInfo.uptime && (
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase">Uptime</label>
                      <p className="mt-1 text-sm text-gray-900 font-mono">
                        {hardwareInfo.uptime}
                      </p>
                    </div>
                  )}
                </>
              )
            })()}
          </div>
        </div>
      )}

      {/* Erro */}
      {error && (
        <div className="card p-4 bg-red-50 border border-red-200">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {[
            { id: TABS.FIREWALL, label: t('routers.management.tabFirewall'), icon: Shield },
            { id: TABS.NAT, label: t('routers.management.tabNat'), icon: Network },
            { id: TABS.ROUTES, label: t('routers.management.tabRoutes'), icon: RouteIcon },
            { id: TABS.INTERFACES, label: t('routers.management.tabInterfaces'), icon: Wifi },
            { id: TABS.TERMINAL, label: t('routers.management.tabTerminal'), icon: Terminal },
          ].map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'px-4 py-2 border-b-2 font-medium text-sm transition-colors',
                  activeTab === tab.id
                    ? 'border-theme-primary text-theme-primary'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </div>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="card p-6">
        {loading && activeTab !== TABS.TERMINAL ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : activeTab === TABS.TERMINAL ? (
          <div className="relative flex flex-col" style={{ height: 'calc(100vh - 420px)', minHeight: '400px' }}>
            <div className={`flex-1 min-h-0 bg-gray-900 text-green-400 font-mono text-sm p-4 rounded-t-lg overflow-y-auto terminal-scroll ${ai.isOpen ? 'mr-[420px]' : ''}`}>
              {terminalHistory.length === 0 ? (
                <div className="text-gray-500">
                  {t('routers.management.terminalIntro')}
                </div>
              ) : (
                terminalHistory.map((item, idx) => (
                  <div key={idx} className="mb-2">
                    {item.type === 'command' && (
                      <div className="text-blue-400">
                        <span className="text-gray-500">&gt;</span>{' '}
                        {item.source === 'ai' && (
                          <span className="text-[10px] font-semibold text-theme-primary bg-theme-primary/20 rounded px-1 py-0.5 mr-1 uppercase">AI</span>
                        )}
                        {item.content}
                      </div>
                    )}
                    {item.type === 'result' && item.content && (
                      <pre className="text-green-400 whitespace-pre-wrap font-mono text-xs">
                        {item.content}
                      </pre>
                    )}
                    {item.type === 'error' && (
                      <div className="text-red-400">
                        {item.content}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className={`flex-none flex gap-2 bg-gray-800 p-3 rounded-b-lg border-t border-gray-700 ${ai.isOpen ? 'mr-[420px]' : ''}`}>
              <input
                type="text"
                value={terminalCommand}
                onChange={(e) => setTerminalCommand(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('routers.management.terminalPlaceholder')}
                className="flex-1 bg-gray-700 text-gray-200 border border-gray-600 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-green-500"
                disabled={executingCommand || !connectionStatus?.connected}
              />
              <button
                onClick={executeTerminalCommand}
                className="px-3 py-2 bg-green-700 hover:bg-green-600 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={executingCommand || !terminalCommand.trim() || !connectionStatus?.connected}
              >
                {executingCommand ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  t('routers.management.execute')
                )}
              </button>
            </div>

            {!ai.isOpen && (
              <TerminalAIButton
                onClick={ai.toggle}
                hasConversation={ai.messages.length > 0}
              />
            )}

            <TerminalAIPanel
              isOpen={ai.isOpen}
              onClose={ai.close}
              messages={ai.messages}
              loading={ai.loading}
              error={ai.error}
              settings={ai.settings}
              onSettingsChange={ai.setSettings}
              tokenStats={ai.tokenStats}
              onSend={ai.sendMessage}
              onRunCommand={ai.runCommand}
              onNewConversation={ai.newConversation}
              suModal={ai.suModal}
              confirmSu={ai.confirmSu}
              cancelSu={ai.cancelSu}
              isLinuxProfile={ai.isLinuxProfile}
              executionPlan={ai.executionPlan}
              approveExecutionPlan={ai.approveExecutionPlan}
              rejectExecutionPlan={ai.rejectExecutionPlan}
              onRetryAsLite={ai.retryAsLite}
            />
          </div>
        ) : (
          <div>
            {/* Barra de ações (botões acima da tabela, estilo Winbox) */}
            {selectedItem && (activeTab === TABS.FIREWALL || activeTab === TABS.NAT || activeTab === TABS.ROUTES || activeTab === TABS.INTERFACES) && (
              <div className="mb-4 p-3 bg-gray-100 border border-gray-300 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">
                    {t('routers.management.selectedItem')} {selectedItem['.id'] || 'N/A'}
                  </span>
                  {selectedItem.name && (
                    <span className="text-sm text-gray-600">
                      ({selectedItem.name})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {canItemBeDisabled(selectedItem) && (
                    <>
                      {isItemDisabled(selectedItem) ? (
                        <button
                          onClick={handleEnableItem}
                          disabled={actionLoading}
                          className="btn btn-success btn-sm"
                          title={t('routers.management.enableItemTitle')}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          {t('routers.management.enable')}
                        </button>
                      ) : (
                        <button
                          onClick={handleDisableItem}
                          disabled={actionLoading}
                          className="btn btn-warning btn-sm"
                          title={t('routers.management.disableItemTitle')}
                        >
                          <Square className="w-4 h-4 mr-1" />
                          {t('routers.management.disable')}
                        </button>
                      )}
                    </>
                  )}
                  {canDelete && (
                    <button
                      onClick={handleDeleteItem}
                      disabled={actionLoading}
                      className="btn btn-error btn-sm"
                      title={t('routers.management.deleteItemTitle')}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {t('common.buttons.delete')}
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedItem(null)}
                    className="btn btn-secondary btn-sm"
                    title={t('routers.management.deselect')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            {activeTab === TABS.FIREWALL && renderTable(data.rules, 'firewall')}
            {activeTab === TABS.NAT && renderTable(data.rules, 'nat')}
            {activeTab === TABS.ROUTES && renderTable(data.routes, 'routes')}
            {activeTab === TABS.INTERFACES && renderTable(data.interfaces, 'interfaces')}
          </div>
        )}
      </div>

      {/* Modal de Configuração */}
      {renderConfigModal()}
    </div>
  )
}

